import { type JSONObject } from 'stores/@types'

import convert from 'convert-units'
import axios, { type AxiosInstance } from 'axios'
import { type MultiFeature } from 'stores/features'
import { makeAutoObservable, observable, runInAction } from 'mobx'
import { type RefObject, useEffect, useRef } from 'react'

const expArr = {
  0: '⁰',
  1: '¹',
  2: '²',
  3: '³',
  4: '⁴',
  5: '⁵',
  6: '⁶',
  7: '⁷',
  8: '⁸',
  9: '⁹'
}

const exp = {
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9'
}

export function formatNumberForMonitor(val: number | undefined): string {
  if (val === undefined || val === null || Number.isNaN(val)) {
    return ' - '
  }

  const fixed = Number(val)?.toFixed(1)
  return Number(fixed).toLocaleString('de-DE')
}

export function getAggregationString(
  aggregates: Record<string, string>
): string {
  return (
    'aggregate=' +
    Object.entries(aggregates)
      .map((el) => el.join('='))
      .join(',')
  )
}

export function avoidInfinity(
  numerator: number | undefined,
  denominator: number | undefined
): string | undefined {
  if (numerator === undefined || denominator === undefined) return undefined
  return denominator === 0 || denominator === undefined
    ? '-'
    : formatNumberForMonitor(numerator / denominator)
}

export function convertToBestBase(val: number | undefined, unit: string) {
  if (val === undefined)
    return {
      unit,
      value: undefined,
      formattedValue: undefined
    }
  const tmp = unit.split('/')
  if (exp[tmp[0].charAt(tmp[0].length - 1)]) {
    tmp[0] = tmp[0].replace(
      tmp[0].charAt(tmp[0].length - 1),
      exp[tmp[0].charAt(tmp[0].length - 1)]
    )
  }
  if (tmp[0] === 't') tmp[0] = 'mt'

  if (tmp[0] === 'm3') {
    return {
      unit,
      value: val,
      formattedValue: formatNumberForMonitor(val)
    }
  }
  const conv = convert(val).from(tmp[0]).toBest({ system: 'metric' })
  const lastChar = conv.unit.charAt(conv.unit.length - 1)
  conv.val = conv.val.toFixed(1)
  if (!isNaN(lastChar)) {
    conv.unit = conv.unit.replace(lastChar, expArr[lastChar])
  }

  tmp[0] = conv.unit === 'mt' ? 't' : conv.unit
  conv.unit = tmp.join('/')

  return {
    unit: conv.unit,
    value: conv.val,
    formattedValue: formatNumberForMonitor(conv.val)
  }
}

export function convertToBest(val: number | undefined, unit: string) {
  /* Yes, because someone didn't understand the Base function
  and refused to fix/adapt it, it became wrapped. */
  if (val === 0) {
    return { unit, value: val, formattedValue: val }
  }
  return convertToBestBase(val, unit)
}

export function getYears(): number[] {
  const lastYear = Number(new Date().getFullYear()) - 1
  const tmp: number[] = []
  const start = 2016
  for (let i = 0; start + i <= lastYear; i++) {
    tmp.push(start + i)
  }
  return tmp
}

export function setFromJSON(
  object: object,
  values: JSONObject,
  except: string[] = [],
  includeUnknownAttributes: boolean = true
): void {
  for (const [key, value] of Object.entries(values)) {
    if ((includeUnknownAttributes || key in object) && !except.includes(key)) {
      object[key] = value
    }
  }
}

export function intOrNull(value: string): number | null {
  const int = parseInt(value)
  return Number.isNaN(int) ? null : int
}

export function floatOrNull(value: string): number | null {
  const float = parseFloat(value)
  return Number.isNaN(float) ? null : float
}

export function createAxiosInstance(
  baseUrl: string,
  token?: string
): AxiosInstance {
  const instance = axios.create({
    baseURL: `${baseUrl}`
  })

  if (token !== undefined)
    instance.defaults.headers.common.Authorization = 'Bearer ' + token
  instance.defaults.headers.common['Content-Type'] = 'application/json'

  return instance
}

export type Aggregate = Record<string, number>
export type GroupBy = Array<Record<string, number>>

export class LazyAggregates {
  public caches = new Map<string, Aggregate | GroupBy | undefined>()
  constructor(
    private readonly feature: MultiFeature,
    private readonly endpoint: string
  ) {
    makeAutoObservable(this)
  }

  aggregate(aggregate: Record<string, string>): Aggregate | undefined {
    return this.query(aggregate, []) as Aggregate
  }

  groupBy(
    aggregate: Record<string, string>,
    groupBy: string[]
  ): GroupBy | undefined {
    return this.query(aggregate, groupBy) as GroupBy
  }

  query(
    aggregate: Record<string, string>,
    groupBy: string[]
  ): Aggregate | GroupBy | undefined {
    const groupString = groupBy.map((el) => '&groupby=' + el).join('')
    const aggString = getAggregationString(aggregate)
    const queryString = `${groupString}&${aggString}`

    if (!this.caches.has(queryString)) {
      this.caches.set(queryString, undefined)
      const url = `/${this.endpoint}/aggregate/?${this.feature.parent.filter}${queryString}`

      void this.feature.parent.client
        .get(url)
        .then((res) =>
          runInAction(() => this.caches.set(queryString, res.data))
        )
    }

    return this.caches.get(queryString)
  }

  reset(): void {
    this.caches = new Map()
  }
}

interface ObjectWithClient {
  client: AxiosInstance
}

export class CachedClient {
  public caches = new Map<string, unknown>()

  constructor(private readonly instance: ObjectWithClient) {
    makeAutoObservable(this, { caches: observable.deep })
  }

  get<ResponseT, ResultT = ResponseT>(
    url: string,
    key?: string,
    sink?: (data: ResponseT) => ResultT,
    loadPaginatedList: boolean = false
  ): ResultT | undefined {
    const cacheKey = key ?? url
    const sinkFunction =
      sink ??
      function (data) {
        return data
      }
    if (!this.caches.has(cacheKey)) {
      this.caches.set(cacheKey, undefined)
      if (loadPaginatedList) {
        void this.getWithPaginationSupport(url)
          .then((res) =>
            runInAction(() => this.caches.set(cacheKey, sinkFunction(res)))
          )
          .catch(() => this.caches.delete(cacheKey))
      } else {
        void this.instance.client
          .get(url)
          .then((res) =>
            runInAction(() => this.caches.set(cacheKey, sinkFunction(res.data)))
          )
          .catch(() => this.caches.delete(cacheKey))
      }
    }

    return this.caches.get(cacheKey) as ResultT | undefined
  }

  getNextLink(links: string): string | undefined {
    const linkArray = links.replaceAll('<', '').replaceAll('>', '').split(',')
    const nextLink = linkArray.find((element: string) =>
      element.includes('rel="next"')
    )
    return nextLink !== null && nextLink !== undefined
      ? nextLink.split(';')[0]
      : undefined
  }

  async getWithPaginationSupport(
    url: string,
    offsetParam: string | undefined = undefined,
    data: undefined | [] = undefined
  ) {
    let result = data ?? []
    const params = offsetParam ?? ''
    await this.instance.client.get(`${url}?${params}`).then((res) => {
      result = result.concat(res.data)
      if ('link' in res.headers) {
        const nextUrl = this.getNextLink(res.headers.link as string)
        if (nextUrl !== null && nextUrl !== undefined) {
          const offsetParams = nextUrl.split('?')[1]
          result = this.getWithPaginationSupport(url, offsetParams, result)
        }
      }
    })
    return result
  }

  reset(cacheKeys?: string[]): void {
    if (cacheKeys === undefined) {
      this.caches = new Map()
    } else {
      cacheKeys.forEach((cacheKey) => {
        this.caches.delete(cacheKey)
      })
    }
  }
}

export function omitNull(obj): object {
  Object.keys(obj)
    .filter((k) => obj[k] === null)
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    .forEach((k) => delete obj[k])
  return obj
}

export function useOutSideClickListener<T extends HTMLElement>(
  callback: () => void
): RefObject<T> {
  const ref = useRef() as RefObject<T>
  useEffect(() => {
    const clickHandler = (event: Event): void => {
      if (ref.current !== null && !ref.current.contains(event.target)) {
        callback()
      }
    }
    document.addEventListener('click', clickHandler)
    return () => {
      document.removeEventListener('click', clickHandler)
    }
  }, [])
  return ref
}
